import { useEffect, useState } from 'react';
import tw from 'twin.macro';
import { useTranslation } from 'react-i18next';

import { Dropdown, IDropdownOption, Option } from '@components/Dropdown';
import {
  SegmentCreatorData,
  useSegmentCreatorContext,
} from '@insights_module/context/SegmentCreatorContext';
import { AQLQueryType } from '@insights_module/adc_ql/types';
import { FilterInput } from '@insights_module/components/FilterInput';
import { useFilterTypeOptions } from '@insights_module/hooks/useFilterTypeOptions';

export const SegmentCreatorAuthorItem = ({ segmentData }: { segmentData: SegmentCreatorData }) => {
  const { negation, value } = segmentData;
  const { t } = useTranslation();
  const { pushSegmentData, isPresentational } = useSegmentCreatorContext();
  const { dropdownOptions } = useFilterTypeOptions();

  const conditionOptions: IDropdownOption[] = [
    {
      value: 'includes',
      label: <Option>{t('insights.query_builder.conditions.is')}</Option>,
    },
    {
      value: 'excludes',
      label: <Option>{t('insights.query_builder.conditions.is_not')}</Option>,
    },
  ];

  const [selectedType, setSelectedType] = useState<IDropdownOption>(
    dropdownOptions.find((item) => item.value === segmentData.type) || dropdownOptions[0]
  );
  const [selectedCondition, setSelectedCondition] = useState<IDropdownOption>(
    conditionOptions.find((item) => item.value === (negation ? 'excludes' : 'includes')) ||
      conditionOptions[0]
  );
  const [selectedAuthor, setSelectedAuthor] = useState<string>((value as string) || '');

  useEffect(() => {
    if (selectedType) {
      const type = selectedType.value as string;
      pushSegmentData({
        ...segmentData,
        type: type as AQLQueryType,
        category: null,
        value: null,
        condition: null,
        negation: false,
      });
    }
  }, [selectedType]);

  useEffect(() => {
    if (selectedCondition && selectedAuthor) {
      const segment = {
        ...segmentData,
        negation: selectedCondition.value === 'excludes',
        value: selectedAuthor,
      };
      pushSegmentData(segment);
    }
  }, [selectedCondition, selectedAuthor]);

  return (
    <div tw='flex gap-1 items-center'>
      <Dropdown
        options={dropdownOptions}
        value={selectedType}
        onChange={(val) => setSelectedType(val as IDropdownOption)}
        labelStyle={tw`bg-spec-author-filter-bg border border-spec-author-filter-stroke text-spec-author-filter-text font-semibold`}
        caretStyle={tw`fill-spec-author-filter-text`}
        dropdownMenuStyle={tw`w-[178px]`}
        isPresentational={isPresentational}
      />

      <Dropdown
        //for now we don't want to show is and is not conditions on creating named entities
        options={conditionOptions.slice(0, 2)}
        value={selectedCondition}
        onChange={(val) => setSelectedCondition(val as IDropdownOption)}
        isPresentational={isPresentational}
      />

      <FilterInput
        focus
        value={selectedAuthor}
        contentEditable={!isPresentational}
        placeholder={t('insights.query_builder.author.placeholder')}
        onPressEnter={(value) => setSelectedAuthor(value as string)}
      />
    </div>
  );
};
